import React, { useEffect, useState } from "react";
import {
  UserPlus,
  PackagePlus,
  RotateCcw,
  Store,
  FileText,
} from "lucide-react";
import { toast } from "react-toastify";
import CustomerForm from "../customers/CustomerForm";
import AddProductModal from "../products/components/AddProductModal";
import AddManualReturnsModal from "../returns/register/components/AddManualReturnsModal";
import VendorForm from "../vendors/VendorForm";
import { AddRecordModal } from "../imei/IMEIList";
import {
  vendorAPI,
  imeiAPI,
  customersAPI,
  productAPI,
  returnRegisterAPI,
} from "../../services";
import "./styles/dashboard.css";
import { useAppSelector } from "../../store/hooks";

const QuickActions = () => {
  const { isPartsURL, isAccessoriesURL } = useAppSelector((state) => state.domain);
  const [isAddCustomerOpen, setIsAddCustomerOpen] = useState(false);
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false);
  const [isImeiModalOpen, setIsImeiModalOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);

  // Load suppliers for IMEI modal
  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const response = await vendorAPI.getVendorsList("", 1, 1000);
        setSuppliers(response.results || []);
      } catch (error) {
        console.error("Error loading suppliers:", error);
      }
    };
    loadSuppliers();
  }, []);

  // Open modal handlers
  const handleAddCustomer = () => setIsAddCustomerOpen(true);
  const handleAddProduct = () => setIsAddProductOpen(true);
  const handleAddReturn = () => setIsReturnModalOpen(true);
  const handleAddVendor = () => setIsVendorModalOpen(true);
  const handleAddImeiRecord = () => setIsImeiModalOpen(true);

  // Close modal handlers
  const handleCloseModal = () => setIsAddCustomerOpen(false);
  const handleCloseProductModal = () => setIsAddProductOpen(false);
  const handleCloseReturnModal = () => setIsReturnModalOpen(false);
  const handleCloseVendorModal = () => setIsVendorModalOpen(false);
  const handleCloseImeiModal = () => setIsImeiModalOpen(false);

  // Save handlers
  const handleSaveCustomer = async (customerData) => {
    try {
      const result = await customersAPI.createCustomer(customerData);
      if (result.error) {
        // Return error to CustomerForm for handling
        return result;
      }
      toast.success("Customer added successfully");
      setIsAddCustomerOpen(false);
      return result;
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error("Failed to add customer");
      return { error: { message: "Failed to add customer" } };
    }
  };

  const handleSaveProduct = async (productData) => {
    try {
      await productAPI.createProduct(productData);
      toast.success("Product added successfully");
      setIsAddProductOpen(false);
    } catch (error) {
      console.error("Error saving product:", error);
      if (error.isValidationError && error.validationErrors) {
        const errorMessages = Object.entries(error.validationErrors)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(", ") : value}`)
          .join(", ");
        toast.error(errorMessages || "Validation failed");
      } else {
        toast.error(error.message || "Failed to add product");
      }
    }
  };

  const handleSaveReturn = async (returnData, returnId) => {
    try {
      let result;
      if (returnId) {
        result = await returnRegisterAPI.updateReturn(returnId, returnData);
      } else {
        result = await returnRegisterAPI.createReturn(returnData);
      }
      
      if (result.error) {
        toast.error(result.error.message || "Failed to save return");
        return;
      }
      
      toast.success(returnId ? "Return updated successfully" : "Return registered successfully");
      setIsReturnModalOpen(false);
    } catch (error) {
      console.error("Error saving return:", error);
      toast.error("Failed to save return");
    }
  };

  const handleSaveVendor = async (vendorData) => {
    try {
      await vendorAPI.createVendor(vendorData);
      toast.success("Supplier added successfully");
      setIsVendorModalOpen(false);
      // Refresh suppliers list for IMEI modal
      const response = await vendorAPI.getVendorsList("", 1, 1000);
      setSuppliers(response.results || []);
    } catch (error) {
      console.error("Error saving supplier:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to add supplier";
      toast.error(errorMessage);
    }
  };

  const handleSaveImeiRecord = async (imeiData) => {
    try {
      await imeiAPI.createImeiRecord(imeiData);
      toast.success("IMEI record created successfully");
      setIsImeiModalOpen(false);
    } catch (error) {
      console.error("Error saving IMEI record:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to create IMEI record";
      toast.error(errorMessage);
    }
  };

  // Quick actions array
  const actions = [
    {
      label: "Add New Customer",
      icon: <UserPlus size={18} />,
      onClick: handleAddCustomer,
    },
    {
      label: "Add Product",
      icon: <PackagePlus size={18} />,
      onClick: handleAddProduct,
    },
    {
      label: "Add Manual Return",
      icon: <RotateCcw size={18} />,
      onClick: handleAddReturn,
    },
    {
      label: "Add Vendor",
      icon: <Store size={18} />,
      onClick: handleAddVendor,
    },
    {
      label: "IMEI Log",
      icon: <FileText size={18} />,
      onClick: handleAddImeiRecord,
    },
  ];

  return (
    <>
      <div
        className="dashboard-card"
        style={{ height: "425px", borderRadius: "10px" }}
      >
        <h3 className="dashboard-card-title">Quick Actions</h3>
        <p className="dashboard-subtitle" style={{ marginBottom: "16px" }}>
          Common tasks
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {actions.map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "16px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #E5E7EB",
                borderRadius: "8px",
                color: isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s ease",
                gap: "12px",
                width: "100%",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = isPartsURL ? "var(--third-color-parts)" : isAccessoriesURL ? "var(--third-color-acc)" : "var(--third-color-mobile)";
                e.currentTarget.style.borderColor = isPartsURL ? "var(--primary-color-parts)" : isAccessoriesURL ? "var(--primary-color-acc)" : "var(--primary-color-mobile)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = "#FFFFFF";
                e.currentTarget.style.borderColor = "#E5E7EB";
              }}
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      </div>

      {isAddCustomerOpen && (
        <CustomerForm onCancel={handleCloseModal} onSave={handleSaveCustomer} />
      )}

      <AddProductModal
        isOpen={isAddProductOpen}
        onClose={handleCloseProductModal}
        onSubmit={handleSaveProduct}
      />

      <AddManualReturnsModal
        isOpen={isReturnModalOpen}
        onClose={handleCloseReturnModal}
        onSubmit={handleSaveReturn}
      />

      {isVendorModalOpen && (
        <VendorForm
          onSave={handleSaveVendor}
          onCancel={handleCloseVendorModal}
        />
      )}

      <AddRecordModal
        open={isImeiModalOpen}
        onClose={handleCloseImeiModal}
        onSubmit={handleSaveImeiRecord}
        suppliers={suppliers}
      />
    </>
  );
};

export default QuickActions;
